// ============================================
// MODAL CLÔTURE DE CAISSE - Version finale
// Une seule croix, temps réel, centralisé
// ============================================

import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { 
  Calculator, AlertTriangle, CheckCircle, RefreshCw, CreditCard, 
  Smartphone, X, Coins, Banknote, TrendingUp, TrendingDown, 
  Minus, ChevronDown, Receipt, ShoppingBag, Save, CheckCircle2
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import { 
  cashClosingService, 
  formatCFA, 
  calculateCoinsTotal, 
  calculateBillsTotal, 
  getVarianceStatus,
  COINS, 
  BILLS, 
  TOLERANCE_THRESHOLD, 
  LARGE_VARIANCE_THRESHOLD 
} from '../services/CashClosingService';

const CashClosingModal = ({ 
  open, 
  onClose, 
  registerId, 
  registerName, 
  sessionId, 
  api, 
  onClosingComplete,
  user
}) => {
  // États
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [theoretical, setTheoretical] = useState(null);
  const [coinCounts, setCoinCounts] = useState({});
  const [billCounts, setBillCounts] = useState({});
  const [notes, setNotes] = useState('');
  const [recountNumber, setRecountNumber] = useState(0);
  const [showRecount, setShowRecount] = useState(false);
  const [salesDetail, setSalesDetail] = useState(null);
  const [showSalesDetail, setShowSalesDetail] = useState(false);
  const [loadingSales, setLoadingSales] = useState(false);
  const [error, setError] = useState(null);

  // Calculs temps réel (useMemo pour performance)
  const coinsTotal = useMemo(() => calculateCoinsTotal(coinCounts), [coinCounts]);
  const billsTotal = useMemo(() => calculateBillsTotal(billCounts), [billCounts]);
  const countedTotal = useMemo(() => coinsTotal + billsTotal, [coinsTotal, billsTotal]);
  const theoreticalCash = useMemo(() => theoretical?.theoretical_cash || 0, [theoretical]);
  const variance = useMemo(() => countedTotal - theoreticalCash, [countedTotal, theoreticalCash]);
  const varianceAbs = useMemo(() => Math.abs(variance), [variance]);
  const varStatus = useMemo(() => getVarianceStatus(variance, countedTotal), [variance, countedTotal]);
  const needsReason = useMemo(() => varianceAbs > TOLERANCE_THRESHOLD, [varianceAbs]);
  const hasVariance = useMemo(() => countedTotal > 0 && variance !== 0, [countedTotal, variance]);

  // Charger les données au montage
  useEffect(() => {
    if (open && registerId) {
      loadData();
    }
  }, [open, registerId]);

  // Charger les données théoriques
  const loadData = useCallback(async () => {
    setError(null);
    const result = await cashClosingService.loadTheoretical(api, registerId);
    if (result.success) {
      setTheoretical(result.data);
    } else {
      setTheoretical(result.data); // Données par défaut
      if (result.error) {
        console.warn('Using default data:', result.error);
      }
    }
  }, [api, registerId]);

  // Charger le détail des ventes
  const loadSalesDetail = useCallback(async () => {
    if (salesDetail) {
      setShowSalesDetail(!showSalesDetail);
      return;
    }
    
    setLoadingSales(true);
    const result = await cashClosingService.loadSalesDetail(api, registerId);
    setLoadingSales(false);
    
    if (result.success) {
      setSalesDetail(result.data);
      setShowSalesDetail(true);
    } else {
      toast.error(result.error);
    }
  }, [api, registerId, salesDetail, showSalesDetail]);

  // Soumettre la clôture
  const handleSubmit = useCallback(async () => {
    // Vérifier si recomptage nécessaire
    if (varianceAbs >= LARGE_VARIANCE_THRESHOLD && recountNumber === 0) {
      setShowRecount(true);
      return;
    }

    // Vérifier motif obligatoire
    if (needsReason && !notes.trim()) {
      toast.error('Veuillez indiquer un motif pour cet écart');
      return;
    }

    setError(null);
    setSaving(true);
    setSaved(false);

    const result = await cashClosingService.createClosing(api, {
      registerId,
      sessionId,
      coinCounts,
      billCounts,
      notes,
      recountNumber,
      theoreticalCash,
      countedTotal,
      variance,
      cardSales: theoretical?.card_sales || 0,
      airtelSales: theoretical?.airtel_money_sales || 0,
      moovSales: theoretical?.moov_money_sales || 0
    });

    setSaving(false);

    if (result.success) {
      setSaved(true);
      toast.success('Clôture enregistrée avec succès');
      
      // Attendre un peu pour montrer l'état "enregistré"
      setTimeout(() => {
        onClosingComplete?.(result.data);
        handleClose();
      }, 800);
    } else {
      setError(result.error);
      toast.error(result.error);
    }
  }, [api, registerId, sessionId, coinCounts, billCounts, notes, recountNumber, 
      theoreticalCash, countedTotal, variance, theoretical, varianceAbs, needsReason, onClosingComplete]);

  // Fermer et réinitialiser
  const handleClose = useCallback(() => {
    setCoinCounts({});
    setBillCounts({});
    setNotes('');
    setTheoretical(null);
    setSalesDetail(null);
    setShowSalesDetail(false);
    setRecountNumber(0);
    setShowRecount(false);
    setError(null);
    setSaved(false);
    onClose();
  }, [onClose]);

  // Gérer le recomptage
  const handleRecount = useCallback(() => {
    setRecountNumber(prev => prev + 1);
    setCoinCounts({});
    setBillCounts({});
    setShowRecount(false);
    toast.info('Recomptage - Soyez précis');
  }, []);

  // Modal de recomptage
  if (showRecount) {
    return (
      <Dialog open={open} onOpenChange={handleClose}>
        <DialogContent className="max-w-md p-0 bg-white rounded-2xl [&>button]:hidden">
          <div className="p-6 text-center">
            <button onClick={handleClose} className="absolute top-4 right-4 w-7 h-7 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center">
              <X className="w-4 h-4 text-gray-500" />
            </button>
            <div className="w-14 h-14 mx-auto mb-4 bg-red-50 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-red-500" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Écart important détecté</h2>
            <p className="text-3xl font-bold text-red-600 mb-3">{formatCFA(varianceAbs)}</p>
            <p className="text-gray-500 mb-6 text-sm">Veuillez recompter les espèces pour confirmer</p>
            <div className="flex gap-3">
              <Button variant="outline" onClick={handleClose} className="flex-1 h-11 rounded-xl">
                Annuler
              </Button>
              <Button onClick={handleRecount} className="flex-1 h-11 rounded-xl bg-gray-900 hover:bg-gray-800">
                Recompter
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent 
        className="max-w-5xl w-[95vw] p-0 overflow-hidden bg-gray-50 rounded-2xl border-0 shadow-2xl flex flex-col [&>button]:hidden" 
        style={{ maxHeight: '85vh' }}
      >
        {/* Header - UNE SEULE croix de fermeture */}
        <div className="px-5 py-3 bg-white border-b border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <h1 className="text-lg font-semibold text-gray-900">Clôture de caisse</h1>
              <Badge variant="secondary" className="text-xs">{registerName || 'Caisse'}</Badge>
              {theoretical?.is_filtered_by_user && (
                <Badge className="text-xs bg-blue-100 text-blue-700">Mes ventes</Badge>
              )}
              {recountNumber > 0 && (
                <Badge className="text-xs bg-amber-100 text-amber-700">Recomptage #{recountNumber}</Badge>
              )}
            </div>
            {/* UNIQUE bouton de fermeture */}
            <button 
              onClick={handleClose} 
              className="w-8 h-8 rounded-full bg-gray-100 hover:bg-gray-200 flex items-center justify-center transition-colors"
              aria-label="Fermer"
            >
              <X className="w-4 h-4 text-gray-500" />
            </button>
          </div>
        </div>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          
          {/* Erreur */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-500 flex-shrink-0" />
              <span className="text-sm text-red-700">{error}</span>
              <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
                <X className="w-4 h-4" />
              </button>
            </div>
          )}

          {/* 3 Cartes principales */}
          <div className="grid grid-cols-3 gap-3">
            {/* Théorique */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Calculator className="w-4 h-4 text-blue-500" />
                <span className="text-xs text-gray-500">Théorique</span>
              </div>
              <p className="text-2xl font-bold text-gray-900">{formatCFA(theoreticalCash)}</p>
              <p className="text-xs text-gray-400 mt-1">
                Fond {formatCFA(theoretical?.starting_cash)} + Ventes {formatCFA(theoretical?.cash_sales)}
              </p>
            </div>

            {/* Compté */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-2">
                <Banknote className="w-4 h-4 text-emerald-500" />
                <span className="text-xs text-gray-500">Compté</span>
              </div>
              <p className={cn("text-2xl font-bold transition-colors", countedTotal > 0 ? "text-gray-900" : "text-gray-300")}>
                {countedTotal > 0 ? formatCFA(countedTotal) : '—'}
              </p>
              <p className="text-xs text-gray-400 mt-1">
                Pièces {formatCFA(coinsTotal)} + Billets {formatCFA(billsTotal)}
              </p>
            </div>

            {/* Écart */}
            <div className={cn("rounded-xl p-4 shadow-sm transition-colors",
              varStatus.color === 'gray' && "bg-gray-100",
              varStatus.color === 'emerald' && "bg-emerald-50",
              varStatus.color === 'amber' && "bg-amber-50",
              varStatus.color === 'red' && "bg-red-50"
            )}>
              <div className="flex items-center gap-2 mb-2">
                {varStatus.status === 'pending' && <Minus className="w-4 h-4 text-gray-400" />}
                {varStatus.status === 'exact' && <CheckCircle className="w-4 h-4 text-emerald-500" />}
                {varStatus.status === 'minor' && (variance > 0 ? <TrendingUp className="w-4 h-4 text-emerald-500" /> : <TrendingDown className="w-4 h-4 text-emerald-500" />)}
                {varStatus.status === 'warning' && <AlertTriangle className="w-4 h-4 text-amber-500" />}
                {varStatus.status === 'critical' && <AlertTriangle className="w-4 h-4 text-red-500" />}
                <span className="text-xs text-gray-500">Écart</span>
              </div>
              <p className={cn("text-2xl font-bold transition-colors",
                varStatus.color === 'gray' && "text-gray-400",
                varStatus.color === 'emerald' && "text-emerald-700",
                varStatus.color === 'amber' && "text-amber-700",
                varStatus.color === 'red' && "text-red-700"
              )}>
                {countedTotal === 0 ? '—' : `${variance >= 0 ? '+' : ''}${formatCFA(variance)}`}
              </p>
              <p className={cn("text-xs mt-1 font-medium",
                varStatus.color === 'emerald' && "text-emerald-600",
                varStatus.color === 'amber' && "text-amber-600",
                varStatus.color === 'red' && "text-red-600"
              )}>
                {varStatus.label}
              </p>
            </div>
          </div>

          {/* Comptage Pièces & Billets */}
          <div className="grid grid-cols-2 gap-3">
            {/* Pièces */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Coins className="w-4 h-4 text-amber-500" />
                <span className="font-medium text-gray-900 text-sm">Pièces</span>
                <span className="ml-auto font-bold text-amber-600 text-sm">{formatCFA(coinsTotal)}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {COINS.map(d => (
                  <div key={d} className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-10">{d}F</span>
                    <Input 
                      type="number" 
                      min="0" 
                      value={coinCounts[d] || ''} 
                      onChange={e => setCoinCounts(p => ({ ...p, [d]: parseInt(e.target.value) || 0 }))}
                      className="h-9 text-center font-semibold rounded-lg text-sm flex-1" 
                      placeholder="0" 
                    />
                  </div>
                ))}
              </div>
            </div>

            {/* Billets */}
            <div className="bg-white rounded-xl p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-3">
                <Banknote className="w-4 h-4 text-emerald-500" />
                <span className="font-medium text-gray-900 text-sm">Billets</span>
                <span className="ml-auto font-bold text-emerald-600 text-sm">{formatCFA(billsTotal)}</span>
              </div>
              <div className="grid grid-cols-2 gap-2">
                {BILLS.map(d => (
                  <div key={d} className="flex items-center gap-2">
                    <span className="text-xs text-gray-500 w-10">{d >= 1000 ? `${d/1000}k` : d}F</span>
                    <Input 
                      type="number" 
                      min="0" 
                      value={billCounts[d] || ''} 
                      onChange={e => setBillCounts(p => ({ ...p, [d]: parseInt(e.target.value) || 0 }))}
                      className="h-9 text-center font-semibold rounded-lg text-sm flex-1" 
                      placeholder="0" 
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Paiements électroniques - Grille compacte */}
          <div className="bg-white rounded-xl p-4 shadow-sm">
            <p className="text-xs text-gray-500 mb-3 font-medium">PAIEMENTS ÉLECTRONIQUES</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                <CreditCard className="w-4 h-4 text-purple-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500">Carte</p>
                  <p className="font-semibold text-sm truncate">{formatCFA(theoretical?.card_sales)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                <Smartphone className="w-4 h-4 text-red-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500">Airtel</p>
                  <p className="font-semibold text-sm truncate">{formatCFA(theoretical?.airtel_money_sales)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                <Smartphone className="w-4 h-4 text-blue-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500">Moov</p>
                  <p className="font-semibold text-sm truncate">{formatCFA(theoretical?.moov_money_sales)}</p>
                </div>
              </div>
              <div className="flex items-center gap-2 p-2 bg-emerald-50 rounded-lg">
                <Receipt className="w-4 h-4 text-emerald-500" />
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-gray-500">Tickets</p>
                  <p className="font-semibold text-sm">{theoretical?.orders_count || 0}</p>
                </div>
              </div>
            </div>
          </div>

          {/* Motif d'écart - UNIQUEMENT si écart détecté */}
          {hasVariance && (
            <div className={cn("rounded-xl p-4 transition-colors", 
              needsReason ? "bg-amber-50 border border-amber-200" : "bg-white shadow-sm"
            )}>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {needsReason ? '⚠️ Motif de l\'écart (obligatoire)' : 'Note sur l\'écart (optionnel)'}
              </label>
              <Textarea 
                value={notes} 
                onChange={e => setNotes(e.target.value)}
                placeholder="Expliquez la raison de l'écart..."
                className="h-16 rounded-lg resize-none text-sm" 
              />
            </div>
          )}

          {/* Détail des ventes - Accordion replié */}
          <Collapsible open={showSalesDetail} onOpenChange={loadSalesDetail}>
            <CollapsibleTrigger asChild>
              <button className="w-full bg-white rounded-xl p-3 shadow-sm flex items-center justify-between hover:bg-gray-50 transition-colors">
                <div className="flex items-center gap-2">
                  <ShoppingBag className="w-4 h-4 text-gray-400" />
                  <span className="text-sm font-medium text-gray-600">Détail de mes ventes</span>
                </div>
                <div className="flex items-center gap-2">
                  {loadingSales && <RefreshCw className="w-3 h-3 animate-spin text-gray-400" />}
                  <ChevronDown className={cn("w-4 h-4 text-gray-400 transition-transform", showSalesDetail && "rotate-180")} />
                </div>
              </button>
            </CollapsibleTrigger>
            <CollapsibleContent>
              {salesDetail && (
                <div className="mt-2 bg-white rounded-xl p-3 shadow-sm max-h-40 overflow-y-auto space-y-1">
                  {(salesDetail.sales || []).slice(0, 10).map((sale, i) => (
                    <div key={sale.id || i} className="flex items-center justify-between py-1.5 px-2 bg-gray-50 rounded text-xs">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-500">#{sale.order_number || i + 1}</span>
                        <span className="text-gray-400">
                          {sale.created_at ? new Date(sale.created_at).toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : ''}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="text-gray-400 capitalize">{sale.payment_method}</span>
                        <span className="font-semibold">{formatCFA(sale.total)}</span>
                      </div>
                    </div>
                  ))}
                  {(salesDetail.sales || []).length === 0 && (
                    <p className="text-center text-gray-400 py-2 text-xs">Aucune vente</p>
                  )}
                </div>
              )}
            </CollapsibleContent>
          </Collapsible>
        </div>

        {/* Footer FIXE avec indicateur de statut */}
        <div className="px-5 py-3 bg-white border-t border-gray-100 flex-shrink-0">
          <div className="flex items-center justify-between gap-4">
            {/* Récap gauche */}
            <div className="hidden sm:flex items-center gap-4 text-xs text-gray-500">
              <span>Théo: <b className="text-gray-700">{formatCFA(theoreticalCash)}</b></span>
              <span>Compté: <b className="text-gray-700">{formatCFA(countedTotal)}</b></span>
              <span className={cn("font-semibold",
                varStatus.color === 'emerald' && "text-emerald-600",
                varStatus.color === 'amber' && "text-amber-600",
                varStatus.color === 'red' && "text-red-600"
              )}>
                Écart: {countedTotal === 0 ? '—' : `${variance >= 0 ? '+' : ''}${formatCFA(variance)}`}
              </span>
            </div>
            
            {/* Boutons */}
            <div className="flex gap-3 w-full sm:w-auto">
              <Button 
                variant="outline" 
                onClick={handleClose} 
                disabled={saving}
                className="flex-1 sm:flex-none h-10 px-5 rounded-xl text-sm"
              >
                Annuler
              </Button>
              <Button 
                onClick={handleSubmit} 
                disabled={loading || saving || saved || (needsReason && !notes.trim()) || countedTotal === 0}
                className={cn(
                  "flex-1 sm:flex-none h-10 px-6 rounded-xl font-semibold text-sm transition-all",
                  saved 
                    ? "bg-emerald-600 hover:bg-emerald-600" 
                    : "bg-gray-900 hover:bg-gray-800",
                  "disabled:bg-gray-200 disabled:text-gray-400"
                )}
              >
                {saving ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Enregistrement...
                  </>
                ) : saved ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                    Enregistré !
                  </>
                ) : (
                  <>
                    <Save className="w-4 h-4 mr-2" />
                    Valider la clôture
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default CashClosingModal;
